import { decryptAttachment } from 'matrix-encrypt-attachment';
import logger from './logger';

/**
 * Decrypt an encrypted media blob from a Matrix content URL.
 *
 * Fetches the encrypted blob from a blob URL, decrypts it using the provided
 * encryption info, and returns a new blob URL pointing to the decrypted content.
 *
 * **Why callers should use `skipCache: true` when downloading encrypted media:**
 * The media cache stores *unencrypted* blobs. Encrypted content downloaded from
 * the homeserver is raw ciphertext that must be decrypted before use. If the
 * ciphertext were cached as-is, a subsequent cache hit would return ciphertext
 * instead of usable media. Callers should bypass the cache for the initial
 * download, decrypt here, and then cache the decrypted blob themselves if needed.
 *
 * @param {string} encryptedBlobUrl - A blob URL pointing to the encrypted data
 * @param {object} encryptionInfo - Matrix encryption metadata (key, iv, hashes, v)
 * @param {string} [mimeType='application/octet-stream'] - MIME type of the decrypted content
 * @returns {Promise<{decryptedBlobUrl: string, decryptedBlob: Blob}>}
 * @throws Will propagate any fetch or decryption errors to the caller
 */
export async function decryptMediaBlob(encryptedBlobUrl, encryptionInfo, mimeType) {
  try {
    logger.debug('[decryptMediaBlob] Decrypting encrypted media');

    const response = await fetch(encryptedBlobUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch encrypted blob: ${response.status} ${response.statusText}`);
    }
    const encryptedArrayBuffer = await response.arrayBuffer();

    const decryptedArrayBuffer = await decryptAttachment(encryptedArrayBuffer, encryptionInfo);

    const decryptedBlob = new Blob([decryptedArrayBuffer], {
      type: mimeType || 'application/octet-stream',
    });

    const decryptedBlobUrl = URL.createObjectURL(decryptedBlob);

    logger.debug('[decryptMediaBlob] Decryption successful');
    return { decryptedBlobUrl, decryptedBlob };
  } finally {
    // Always revoke the encrypted blob URL — the caller only needs the decrypted one
    URL.revokeObjectURL(encryptedBlobUrl);
  }
}
